import { useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStop } from '@fortawesome/free-solid-svg-icons';
import { useTimer } from '../../hooks/useTimerStore';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import { CONFIG } from '../../config';
import s from './GlobalFloatingTimer.module.css';

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const sec = totalSeconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function GlobalFloatingTimer() {
  const { timer, stopTimer, updatePos } = useTimer();
  const { user } = useAuth();
  const { showToast } = useToast();

  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  // Ref keeps the latest timer state accessible synchronously in handleStop
  const timerRef = useRef(timer);
  useEffect(() => {
    timerRef.current = timer;
  }, [timer]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button')) return;
      dragging.current = true;
      dragOffset.current = { x: e.clientX - timer.pos.x, y: e.clientY - timer.pos.y };
      e.preventDefault();
    },
    [timer.pos],
  );

  useEffect(() => {
    if (!timer.active) return;

    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      updatePos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const onUp = () => {
      dragging.current = false;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [timer.active, updatePos]);

  const handleStop = useCallback(async () => {
    const t = timerRef.current;
    if (!t.active) return;

    // Capture snapshot inline — do NOT call stopTimer() yet so the timer survives a save failure
    const endTime = Date.now();
    const elapsed = endTime - t.startTime;
    const totalMinutes = Math.floor(elapsed / 60_000) + (elapsed % 60_000 > 0 ? 1 : 0);
    const manualUnitsVal = parseInt(t.manualUnits, 10);
    const units = isNaN(manualUnitsVal) ? Math.ceil(totalMinutes / 6) : manualUnitsVal;

    try {
      if (CONFIG.TR_BILLING_V2_FORM_ENABLED) {
        await api.post('/api/time-entries', {
          caseRef: t.caseRef,
          userId: user?.sub ?? user?.username ?? 'unknown',
          startedAt: new Date(t.startTime).toISOString(),
          endedAt: new Date(endTime).toISOString(),
          durationSeconds: Math.floor(elapsed / 1000),
          units,
          // Fallback covers timers started before description was required
          taskDescription: t.taskDescription || 'Timer entry',
          activityCodeId: t.activityCodeId ?? null,
          billable: t.billable ?? true,
          source: 'timer',
        });
      } else {
        await api.post(`/api/casefile/${t.caseRef}/timeentry`, {
          Duration: formatElapsed(elapsed),
          StartTime: new Date(t.startTime).toISOString(),
          EndTime: new Date(endTime).toISOString(),
          Task: t.taskDescription,
          Units: units,
          User: user?.username ?? 'Unknown',
        });
      }
      // Clear the timer only after a successful save
      stopTimer();
      showToast('Time entry recorded', 'success');
    } catch (err: unknown) {
      // Timer is intentionally kept running so the user can retry
      const axiosErr = err as {
        response?: { data?: { error?: { message?: string }; message?: string } };
      };
      const serverMsg =
        axiosErr.response?.data?.error?.message ?? axiosErr.response?.data?.message;
      showToast(
        serverMsg ? `Could not save: ${serverMsg}` : 'Failed to save — timer kept running, please try again',
        'error',
      );
    }
  }, [stopTimer, user, showToast]);

  if (!timer.active) return null;

  return (
    <div
      className={s.floatingTimer}
      style={{ left: timer.pos.x, top: timer.pos.y }}
      onMouseDown={onMouseDown}
    >
      <div>
        <div className={s.caseLabel}>{timer.caseRef}</div>
        <div className={s.timerText}>{formatElapsed(timer.elapsed)}</div>
      </div>
      <button className={s.stopBtn} onClick={handleStop}>
        <FontAwesomeIcon icon={faStop} /> Stop
      </button>
    </div>
  );
}
